import { Animated, Dimensions, Pressable, StyleSheet, View } from "react-native";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

interface Ref {
  toggleSidebar: () => void;
}

interface Props {
  children?: React.ReactNode;
}
const SmallScreenSidebar = React.forwardRef<Ref, Props>((props, ref) => {
  const [width, setWindowWidth] = useState(Dimensions.get("window").width);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const sidebarAnim = useRef(new Animated.Value(-width)).current;
  const backgroundAnim = useRef(new Animated.Value(0)).current;

  const toggleSidebar = useCallback(() => {
    Animated.parallel([
      Animated.timing(sidebarAnim, {
        toValue: isSidebarVisible ? -width : 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(backgroundAnim, {
        toValue: isSidebarVisible ? 0 : 1,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();
    setIsSidebarVisible(!isSidebarVisible);
  }, [isSidebarVisible, sidebarAnim, backgroundAnim]);

  useImperativeHandle(
    ref,
    () => ({
      toggleSidebar,
    }),
    [toggleSidebar],
  );


  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setWindowWidth(window.width);
    })

    setWindowWidth(Dimensions.get("window").width);
    return () => {
      subscription.remove()
    }
  }, []);

  const isSmallScreen = width < 1024;
  if (!isSmallScreen) {
    return null;
  }

  return (
    <>
      {isSidebarVisible ? (
        <Animated.View
          style={[
            styles.background,
            {
              opacity: backgroundAnim,
            },
          ]}
        >
          <Pressable
            onPress={toggleSidebar}
            className="bg-black/40"
            style={styles.backgroundTouchable}
          ></Pressable>
        </Animated.View>
      ) : null}
      <Animated.View
        style={[
          styles.sidebar,
          {
            width: Math.min(width * 0.8, 300),
            transform: [{ translateX: sidebarAnim }],
          },
        ]}
      >
        <View className="absolute top-0 right-0 bottom-0 min-w-full">
          {props.children}
        </View>
      </Animated.View>
    </>
  );
});

const styles = StyleSheet.create({
  background: {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 1,
  },
  backgroundTouchable: {
    flex: 1,
  },
  sidebar: {
    bottom: 0,
    left: 0,
    position: "absolute",
    top: 0,
    zIndex: 2,
  },
});

SmallScreenSidebar.displayName = "SmallScreenSidebar";
export { SmallScreenSidebar };
